import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getPublicBookingPresentation,
  getPublicMeetingUrlLabel,
  getSafePublicMeetingUrl,
  isConfirmedPublicBooking,
  mergeBookingPendingState,
  normalizeBookingNavigationState
} from "../../utils/publicBookingStatus";

const API_URL = window.location.hostname === "localhost"
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173")
  : window.location.origin;

const DISPLAY_TIMEZONE = "Asia/Tokyo";

function formatDateTime(value) {
  if (!value) return "未設定";
  return new Date(value).toLocaleString("ja-JP", { timeZone: DISPLAY_TIMEZONE });
}

export default function PublicBookingManagePage() {
  const { publicId, appointmentPublicId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [appointment, setAppointment] = useState(null);
  const [pendingState, setPendingState] = useState(() => normalizeBookingNavigationState(location.state));
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchAppointment = useCallback(async () => {
    setIsRefreshing(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/public/booking/appointments/${encodeURIComponent(appointmentPublicId)}`);
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.error || "予約情報の取得に失敗しました。");
      }
      setAppointment(json.appointment);
      setPendingState((current) => mergeBookingPendingState(current, json));
    } catch (fetchError) {
      setError(fetchError.message || "予約情報の取得に失敗しました。");
    } finally {
      setIsRefreshing(false);
    }
  }, [appointmentPublicId]);

  useEffect(() => {
    fetchAppointment();
  }, [fetchAppointment]);

  const presentation = getPublicBookingPresentation({
    appointment,
    bookingPending: pendingState.bookingPending
  });
  const bookingPending = presentation.key === "pending";
  const bookingConfirmed = isConfirmedPublicBooking(appointment);
  const meetingUrlLabel = getPublicMeetingUrlLabel({ appointment, bookingPending });
  const safeMeetingUrl = bookingConfirmed ? getSafePublicMeetingUrl(appointment?.meeting_url) : "";

  const canCancel = useMemo(() => {
    return appointment?.status === "confirmed" || appointment?.status === "pending";
  }, [appointment]);
  const canReschedule = bookingConfirmed;

  const handleCancel = async () => {
    if (!canCancel || isCancelling) return;
    setIsCancelling(true);
    setError("");
    setNotice("");

    try {
      const res = await fetch(`${API_URL}/api/public/booking/appointments/${encodeURIComponent(appointmentPublicId)}/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({})
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.error || "キャンセルに失敗しました。");
      }
      setAppointment(json.appointment);
      setNotice("予約をキャンセルしました。");
    } catch (cancelError) {
      setError(cancelError.message || "キャンセルに失敗しました。");
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_#e0f2fe,_transparent_32%),linear-gradient(180deg,_#f8fafc_0%,_#eef2ff_100%)] px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-3xl space-y-5">
        <div className="rounded-[28px] bg-white/92 p-6 shadow-sm ring-1 ring-white/60 backdrop-blur sm:rounded-[32px]">
          <p className="text-sm font-semibold text-blue-600">Online Booking</p>
          <h1 className="mt-2 text-2xl font-bold text-gray-900 sm:text-3xl">予約内容の確認・変更</h1>
          <p className="mt-3 text-sm leading-6 text-gray-600">
            現在の予約内容を確認し、必要に応じて日程変更またはキャンセルを行えます。
          </p>
        </div>

        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        ) : null}
        {notice ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{notice}</div>
        ) : null}

        <div className="rounded-[28px] border border-white/70 bg-white/92 p-5 shadow-sm backdrop-blur sm:rounded-[32px] sm:p-6">
          <dl className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <dt className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">状態</dt>
              <dd className="mt-2 text-sm font-bold text-gray-900">{presentation.statusLabel}</dd>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
              <dt className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">日時</dt>
              <dd className="mt-2 text-sm font-bold text-gray-900">{formatDateTime(appointment?.started_at)}</dd>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white p-4">
              <dt className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">予約者</dt>
              <dd className="mt-2 text-sm text-gray-700">{appointment?.invitee_name || "読み込み中"}</dd>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white p-4">
              <dt className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">メール</dt>
              <dd className="mt-2 break-all text-sm text-gray-700">{appointment?.invitee_email || "読み込み中"}</dd>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:col-span-2">
              <dt className="text-xs font-black uppercase tracking-[0.18em] text-gray-400">会議 URL</dt>
              <dd className="mt-2 break-all text-sm text-gray-700">
                {safeMeetingUrl ? (
                  <a href={safeMeetingUrl} target="_blank" rel="noreferrer" className="font-bold text-blue-600 hover:text-blue-700">
                    {meetingUrlLabel}
                  </a>
                ) : meetingUrlLabel}
              </dd>
            </div>
          </dl>
        </div>

        {bookingPending ? (
          <div className="rounded-[28px] border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-900 shadow-sm sm:rounded-[32px] sm:p-6" role="status">
            <h2 className="font-bold">Zoom会議URLの発行を確認中です</h2>
            {pendingState.warning ? <p className="mt-2">{pendingState.warning}</p> : null}
            <p className="mt-2">この時間枠は確保されています。確認メールはまだ送信されていないため、予約フォームの再送信はしないでください。</p>
            <p className="mt-2">主催者は予約管理画面から再試行できます。長時間変わらない場合は、予約IDを添えて案内元へご連絡ください。</p>
            <button
              type="button"
              onClick={fetchAppointment}
              disabled={isRefreshing}
              className="mt-4 rounded-2xl border border-amber-300 bg-white px-4 py-2.5 text-sm font-bold text-amber-800 transition hover:bg-amber-100 disabled:opacity-60"
            >
              {isRefreshing ? "確認中..." : "状態を再確認"}
            </button>
          </div>
        ) : null}

        <div className="rounded-[28px] border border-white/70 bg-white/92 p-5 shadow-sm backdrop-blur sm:rounded-[32px] sm:p-6">
          <h2 className="text-lg font-bold text-gray-900">操作</h2>
          <p className="mt-2 text-sm text-gray-500">
            {bookingPending
              ? "Zoom URLの準備中は重複予約を防ぐため日程変更できません。必要な場合は予約をキャンセルできます。"
              : bookingConfirmed
                ? "日程変更またはキャンセルができます。"
                : "確定済みの予約だけ日程変更とキャンセルができます。"}
          </p>

          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => navigate(`/public/booking/${publicId}/reschedule/${appointmentPublicId}`)}
              disabled={!canReschedule}
              className="rounded-2xl bg-gray-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              日程変更する
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={!canCancel || isCancelling}
              className="rounded-2xl border border-rose-200 px-4 py-3 text-sm font-bold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
            >
              {isCancelling ? "キャンセル中..." : "この予約をキャンセル"}
            </button>
            <Link
              to={`/public/booking/${publicId}/complete/${appointmentPublicId}`}
              state={normalizeBookingNavigationState({ bookingPending, warning: pendingState.warning })}
              className="rounded-2xl border border-gray-200 px-4 py-3 text-center text-sm font-bold text-gray-700 transition hover:bg-gray-50"
            >
              完了画面へ戻る
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
